export type GuideSection = {
  heading: string;
  paragraphs: string[];
  bullets?: string[];
};

export type GuideContent = {
  slug: string;
  title: string;
  description: string;
  intro: string;
  sections: GuideSection[];
  ctaLabel: string;
};

export const MUSIC_GUIDES: GuideContent[] = [
  {
    slug: 'music-release-workflow',
    title: 'Indie Music Release Workflow Checklist',
    description:
      'A practical release workflow for indie artists: organize demos, mixes, artwork, and promo in one place before distribution.',
    intro:
      'Finishing a release is more than mixing a song. You need demos, masters, artwork, promo copy, and a clear status for every track. Cassette gives indie musicians a single workspace from first idea to upload-ready — with files stored in your own Google Drive.',
    sections: [
      {
        heading: 'Why a release workflow matters',
        paragraphs: [
          'Most abandoned projects fail from disorganization, not lack of talent. Scattered Drive folders, lost demo versions, and missing artwork delay releases by months.',
          'A structured workflow keeps every asset tied to a track and every track moving through visible stages: Draft, In Progress, Mixing, Complete, Published.',
        ],
      },
      {
        heading: 'The Cassette release checklist',
        bullets: [
          'Create a track project and upload your latest demo or mix',
          'Add artwork (cover, social crops, banners) in the track artwork section',
          'Collect promo materials: press notes, link-in-bio assets, vertical videos',
          'Update track status as you move through mixing and mastering',
          'Invite your producer or engineer as a collaborator for feedback',
          'When ready, export metadata and assets for DistroKid, TuneCore, or CD Baby',
        ],
      },
      {
        heading: 'Ship faster without a label ops team',
        paragraphs: [
          'Cassette is not a distributor — it helps you finish and organize before you upload. Think of it as your project manager: what is done, what is missing, and who needs to deliver next.',
          'The free Starter plan includes 3 active tracks. Artist ($5/mo) adds collaboration for bands and producer partnerships.',
        ],
      },
    ],
    ctaLabel: 'Start your release workflow — free',
  },
  {
    slug: 'organize-music-demos',
    title: 'How to Organize Music Demos in Google Drive',
    description:
      'Stop losing demo versions. Organize audio takes, mixes, and notes per track with Cassette and Google Drive.',
    intro:
      'Demo chaos kills momentum: demo_v3_final_FINAL.wav in one folder, notes in another, artwork somewhere else. Cassette structures every track as a project while your files stay in your Google Drive.',
    sections: [
      {
        heading: 'The problem with flat Drive folders',
        paragraphs: [
          'Google Drive is great storage but weak project structure. Without per-track organization you forget which demo you sent the mixer, which take has the best vocal, and whether artwork is final.',
        ],
      },
      {
        heading: 'How Cassette organizes demos',
        bullets: [
          'One track = one project with audio, artwork, promo, and notes',
          'Upload multiple takes; mark a preview version for collaborators',
          'Kanban board shows which tracks are stuck in Demo vs Mixing',
          'OAuth per user — your Drive, your folders, no service account',
          'PWA install: add demos from your phone on the go',
        ],
      },
      {
        heading: 'Best practices',
        paragraphs: [
          'Name tracks clearly (song title + artist). Add release date when you have one. Use notes for mix feedback and reference links.',
          'Sync regularly after manual Drive changes to avoid drift between app and cloud.',
        ],
      },
    ],
    ctaLabel: 'Organize your demos — create free account',
  },
  {
    slug: 'collaborate-with-producer',
    title: 'Share Music Tracks with Your Producer or Band',
    description:
      'Collaborate on tracks with viewers and editors. Share demos, artwork, and progress without emailing zip files.',
    intro:
      'Modern music is collaborative. Cassette lets you invite bandmates, mixing engineers, and producers to the same track — with role-based access and real-time audio comments.',
    sections: [
      {
        heading: 'Collaboration roles',
        bullets: [
          'Viewers: see all track files and progress (free for all users)',
          'Editors: upload and edit files (Artist tier and above)',
          'Share via link with optional invite flow',
        ],
      },
      {
        heading: 'When to use Cassette vs email or WeTransfer',
        paragraphs: [
          'Email threads lose context. WeTransfer links expire. Cassette keeps the full history on the track: every demo version, artwork revision, and comment at a timestamp on the waveform.',
          'Perfect for producer–artist pairs, bands with remote members, and engineers reviewing mixes.',
        ],
      },
      {
        heading: 'Getting started with collaborators',
        paragraphs: [
          'Open any track → Manage Collaborators → invite by email. Your collaborator signs in with Google or Apple and sees shared tracks in their dashboard.',
          'Studio tier ($25/mo) offers unlimited tracks and unlimited collaborators for busy producers.',
        ],
      },
    ],
    ctaLabel: 'Invite your team — sign up free',
  },
  {
    slug: 'mcp-cursor-claude',
    title: 'Cassette MCP: Music Workflow in Cursor & Claude',
    description:
      'Connect Cursor or Claude Desktop to Cassette via @cassette.diy/mcp. Create tracks, upload files, and manage collaboration from your AI assistant.',
    intro:
      'Cassette exposes an MCP server so AI assistants can run your release workflow programmatically: create tracks, upload audio and artwork, update metadata, and verify files — while everything stays in your Google Drive.',
    sections: [
      {
        heading: 'What is Cassette MCP?',
        paragraphs: [
          'MCP (Model Context Protocol) lets tools like Cursor and Claude Desktop call Cassette on your behalf. Install @cassette.diy/mcp via npx, add a personal access token from Settings, and your assistant can operate your account with scoped permissions.',
        ],
      },
      {
        heading: 'Example music workflows',
        bullets: [
          '“Create a track called Summer Demo and set the artist name”',
          '“Upload this WAV as the main audio for my latest track”',
          '“Add artwork from ~/Downloads/cover.jpg”',
          '“Update the Spotify pitch and release notes”',
          '“List all files uploaded to track X”',
        ],
      },
      {
        heading: 'Setup in 2 minutes',
        paragraphs: [
          'Sign in → Settings → API / AI access → create token with music:read and music:write scopes. Copy the MCP config snippet (uses npx -y @cassette.diy/mcp) into ~/.cursor/mcp.json. See the full tool catalog on our MCP docs page.',
        ],
      },
    ],
    ctaLabel: 'Read MCP setup docs',
  },
  {
    slug: 'pwa-install',
    title: 'Install Cassette as a PWA on Phone or Desktop',
    description:
      'Install Cassette without the App Store. Add the music workflow PWA to your home screen on iOS, Android, or desktop.',
    intro:
      'Cassette is a Progressive Web App (PWA). You get an app-like experience — fast launch, home screen icon, mobile-first UI — without waiting for App Store approval.',
    sections: [
      {
        heading: 'Why install the PWA?',
        bullets: [
          'Quick access from home screen or dock',
          'Mobile-first UI for adding demos and checking progress on the go',
          'No App Store account required',
          'Same account and Google Drive sync as the web app',
        ],
      },
      {
        heading: 'How to install',
        paragraphs: [
          'On Chrome (desktop/Android): open cassette.diy → Install icon in the address bar or browser menu → Install Cassette.',
          'On Safari (iOS): Share → Add to Home Screen. On supported browsers you may also see an in-app install prompt.',
        ],
      },
      {
        heading: 'Offline and updates',
        paragraphs: [
          'Core assets are cached for faster loads. You need connectivity for uploads and sync. The app updates automatically when you open it after a new version ships.',
        ],
      },
    ],
    ctaLabel: 'Open Cassette and install',
  },
];

export function getGuideBySlug(slug: string): GuideContent | undefined {
  return MUSIC_GUIDES.find((g) => g.slug === slug);
}

export function guidePath(slug: string): string {
  return `/guides/${slug}`;
}
